import React from 'react';

import { isHorizontalCommunityTemplateEnabled as isCommunityNavigationEnabledSelector } from '@lumapps/communities/ducks/selectors';
import { customerIdSelector } from '@lumapps/customer/ducks/selectors';
import { isTagzEnabled as isTagzEnabledSelector } from '@lumapps/folksonomy/ducks/selectors';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { currentLanguageSelector } from '@lumapps/languages/ducks/selectors';
import { NannyAlertAction } from '@lumapps/nanny-details/ducks/slice';
import { handleNannyError } from '@lumapps/nanny-details/utils/errorHandler';
import { isNannyEnabled } from '@lumapps/nanny/ducks/selectors';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { connectedUserSelector } from '@lumapps/user/ducks/selectors';
import { UserAccountType, User } from '@lumapps/user/types';
import { Suspense } from '@lumapps/utils/components/Suspense';
import { useLazyLoadingProgress } from '@lumapps/utils/hooks/useLazyLoadingProgress';

import { creationDialogAvailableStepsSelector } from '../../ducks/dialogSelectors';
import { CommunityCreationDialogSteps } from '../../types';
import { CommunityCreationDialogProps } from './CommunityCreationDialog';

const LazyCommunityCreationDialog = React.lazy(
    () =>
        import(
            /* webpackChunkName: 'community-creation-dialog' */
            './CommunityCreationDialog'
        ),
);

export const CommunityCreationDialog: React.FC<
    Pick<CommunityCreationDialogProps, 'isOpen' | 'isSpace' | 'onClose' | 'hideBulkCreate'>
> = ({ isOpen, ...communityCreationDialogProps }) => {
    const dispatch = useDispatch();
    const displayedSteps = useSelector(creationDialogAvailableStepsSelector(communityCreationDialogProps.isSpace));
    const customerId = useSelector(customerIdSelector);
    const instanceId = useSelector(instanceIdSelector);
    const currentUser = useSelector(connectedUserSelector) as User;
    const currentLanguage = useSelector(currentLanguageSelector);
    const isTagzEnabled = useSelector(isTagzEnabledSelector);
    const isHorizontalCommunityTemplateEnabled = useSelector(isCommunityNavigationEnabledSelector);
    const isNannyFeatureEnabled = useSelector(isNannyEnabled);

    const { onComponentLoaded, isLoaded } = useLazyLoadingProgress(isOpen);

    const handleNanny = React.useCallback((error: NannyAlertAction) => dispatch(handleNannyError(error)), [dispatch]);

    if (!isLoaded && !isOpen) {
        return null;
    }

    return (
        <Suspense isDialog>
            <LazyCommunityCreationDialog
                {...communityCreationDialogProps}
                currentLanguage={currentLanguage}
                currentUser={currentUser}
                customerId={customerId}
                displayedSteps={displayedSteps as CommunityCreationDialogSteps[]}
                instanceId={instanceId}
                isCurrentUserAccountTypeMicrosoft={currentUser.accountType === UserAccountType.MICROSOFT}
                isHorizontalCommunityTemplateEnabled={isHorizontalCommunityTemplateEnabled}
                isNannyFeatureEnabled={isNannyFeatureEnabled}
                isOpen={isOpen}
                isTagzEnabled={isTagzEnabled}
                onNannyError={handleNanny}
                onOpen={onComponentLoaded}
            />
        </Suspense>
    );
};
